<template>
  <div class="mobile-tooltip">
    <div
      class="mobile-tooltip__trigger"
      @touchstart="handleTouchStart"
      @touchend="handleTouchEnd"
      @click="handleClick"
    >
      <slot name="activator" />
    </div>

    <transition name="mobile-tooltip">
      <div v-if="isVisible" class="mobile-tooltip__content">
        <div class="mobile-tooltip__text">
          <slot />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "MobileTooltip",
  props: {
    position: {
      type: String,
      default: "top",
      validator: (value) => ["top", "bottom", "left", "right"].includes(value),
    },
    delay: {
      type: Number,
      default: 300,
    },
    duration: {
      type: Number,
      default: 3000,
    },
  },
  data() {
    return {
      isVisible: false,
      touchTimer: null,
      hideTimer: null,
    };
  },

  mounted() {
    // Добавляем обработчики клика и тапа по документу
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("touchstart", this.handleDocumentClick);
  },

  beforeDestroy() {
    this.clearTimers();
    // Удаляем обработчики клика и тапа по документу
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("touchstart", this.handleDocumentClick);
  },

  methods: {
    handleTouchStart(event) {
      // Предотвращаем всплытие события
      event.stopPropagation();
      // Очищаем предыдущие таймеры
      this.clearTimers();

      // Запускаем таймер для показа tooltip
      this.touchTimer = setTimeout(() => {
        this.showTooltip();
      }, this.delay);
    },

    handleTouchEnd(event) {
      // Предотвращаем всплытие события
      event.stopPropagation();
      // Очищаем таймер показа
      if (this.touchTimer) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
      }
    },

    handleClick(event) {
      // Предотвращаем всплытие события
      event.stopPropagation();
      // При клике показываем tooltip сразу
      this.clearTimers();
      this.showTooltip();
    },

    handleDocumentClick(event) {
      // Проверяем, что клик был не по нашему компоненту
      if (!this.$el.contains(event.target)) {
        this.hideTooltip();
      }
    },

    showTooltip() {
      this.isVisible = true;

      // Позиционируем tooltip правильно
      this.$nextTick(() => {
        this.positionTooltip();
      });

      // Автоматически скрываем через указанное время
      this.hideTimer = setTimeout(() => {
        this.hideTooltip();
      }, this.duration);
    },

    positionTooltip() {
      const trigger = this.$el.querySelector(".mobile-tooltip__trigger");
      const content = this.$el.querySelector(".mobile-tooltip__content");

      if (!trigger || !content) return;

      const triggerRect = trigger.getBoundingClientRect();
      const contentRect = content.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let left, top;

      switch (this.position) {
        case "top":
          left =
            triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
          top = triggerRect.top - contentRect.height - 8;
          break;
        case "bottom":
          left =
            triggerRect.left + triggerRect.width / 2 - contentRect.width / 2;
          top = triggerRect.bottom + 8;
          break;
        case "left":
          left = triggerRect.left - contentRect.width - 8;
          top =
            triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
          break;
        case "right":
          left = triggerRect.right + 8;
          top =
            triggerRect.top + triggerRect.height / 2 - contentRect.height / 2;
          break;
      }

      // Проверяем границы viewport
      if (left < 10) left = 10;
      if (left + contentRect.width > viewportWidth - 10) {
        left = viewportWidth - contentRect.width - 10;
      }
      if (top < 10) top = 10;
      if (top + contentRect.height > viewportHeight - 10) {
        top = viewportHeight - contentRect.height - 10;
      }

      content.style.left = `${left}px`;
      content.style.top = `${top}px`;
    },

    hideTooltip() {
      this.isVisible = false;
    },

    clearTimers() {
      if (this.touchTimer) {
        clearTimeout(this.touchTimer);
        this.touchTimer = null;
      }
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.mobile-tooltip {
  position: relative;
  display: inline-block;

  &__trigger {
    cursor: pointer;
    user-select: none;
  }

  &__content {
    position: fixed !important;
    z-index: 99999 !important;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.4;
    min-width: 200px;
    max-width: 350px;
    width: max-content;
    word-wrap: break-word;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    pointer-events: none;

    // Позиционирование управляется через JavaScript
  }

  &__text {
    white-space: pre-line;
  }
}

// Анимации
.mobile-tooltip-enter-active,
.mobile-tooltip-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.mobile-tooltip-enter,
.mobile-tooltip-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

.mobile-tooltip-enter-to,
.mobile-tooltip-leave {
  opacity: 1;
  transform: scale(1);
}
</style>
